import { cn } from "@utils/cn";
import { VariantProps, cva } from "class-variance-authority";
import React from "react";

const alertVariants = cva(
  "flex p-4 justify-start items-center gap-4 rounded-lg shadow-[0px_1px_0px_0px_rgba(0,0,0,0.10),0px_2px_2px_0px_rgba(0,0,0,0.05)]",
  {
    variants: {
      variant: {
        info: "bg-[#FBDB8C] text-black",
        warning: "bg-[#DE5558] text-white",
        confirm: "bg-[#1EAB56] text-white",
        tip: "bg-[#F1F8FF] text-black",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  children: React.ReactNode;
  hideIcon?: boolean;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, children, hideIcon, ...props }, ref) => {
    const icons = {
      info: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
        >
          <path
            opacity="0.6"
            d="M16 26C21.5228 26 26 21.5228 26 16C26 10.4772 21.5228 6 16 6C10.4772 6 6 10.4772 6 16C6 21.5228 10.4772 26 16 26Z"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            opacity="0.6"
            d="M16 20V16"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            opacity="0.6"
            d="M16 12H16.01"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      warning: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
        >
          <path
            d="M16 26C21.5228 26 26 21.5228 26 16C26 10.4772 21.5228 6 16 6C10.4772 6 6 10.4772 6 16C6 21.5228 10.4772 26 16 26Z"
            stroke="white"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 20V16"
            stroke="white"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 12H16.01"
            stroke="white"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      confirm: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
        >
          <path
            d="M24 10L13 21L8 16"
            stroke="white"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      tip: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
        >
          <path
            opacity="0.6"
            d="M15 16H17C17.5304 16 18.0391 15.7893 18.4142 15.4142C18.7893 15.0391 19 14.5304 19 14C19 13.4696 18.7893 12.9609 18.4142 12.5858C18.0391 12.2107 17.5304 12 17 12H14C13.4 12 12.9 12.2 12.6 12.6L7 18"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            opacity="0.6"
            d="M11 22L12.6 20.6C12.9 20.2 13.4 20 14 20H18C19.1 20 20.1 19.6 20.8 18.8L25.4 14.4C25.7859 14.0353 26.0111 13.5323 26.0261 13.0016C26.0411 12.4708 25.8447 11.9559 25.48 11.57C25.1153 11.1841 24.6123 10.9589 24.0816 10.9439C23.5508 10.9289 23.0359 11.1253 22.65 11.49L18.45 15.39"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            opacity="0.6"
            d="M6 17L12 23"
            stroke="black"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    };

    return (
      <div
        className={cn(alertVariants({ variant, className }))}
        ref={ref}
        {...props}
      >
        {!hideIcon && (
          <div className="flex-shrink-0">{icons[variant || "info"]}</div>
        )}
        <div className="font-instrument text-sm font-medium leading-[150%] opacity-60">
          {children}
        </div>
      </div>
    );
  }
);

Alert.displayName = "Alert";

export { Alert, alertVariants };
